import type { SupportTicketOwn } from '@travel-planner/shared/schemas';
import { UTCDate } from '@date-fns/utc';
import { SUPPORT_OWN_EDIT_WINDOW_MINUTES } from '@travel-planner/shared/schemas';
import { useEffect, useState } from 'react';

const TIMEZONE_MARKER = /[zZ]$|[+-]\d{2}:?\d{2}$/;
const WINDOW_MS = SUPPORT_OWN_EDIT_WINDOW_MINUTES * 60_000;

// `createdOn` arrives as a naked Postgres UTC timestamp; NaN when unparseable.
export const ownTicketEditDeadline = (createdOn: string): number => {
  const iso = TIMEZONE_MARKER.test(createdOn)
    ? createdOn
    : `${createdOn.replace(' ', 'T')}Z`;
  return new UTCDate(iso).getTime() + WINDOW_MS;
};

// Mirrors the server guard on `support.updateOwn`; the server stays the
// authority (a skewed client clock only shows/hides the button early).
export const ownTicketEditMsLeft = (
  ticket: Pick<SupportTicketOwn, 'createdOn' | 'status'>,
  now: number
): number => {
  if (ticket.status !== 'open') return 0;
  const left = ownTicketEditDeadline(ticket.createdOn) - now;
  return Number.isFinite(left) && left > 0 ? left : 0;
};

// Remaining edit time, re-evaluated every few seconds while the window is
// open so the countdown and the Edit button retire on their own.
export const useOwnTicketEditWindow = (
  ticket: Pick<SupportTicketOwn, 'createdOn' | 'status'>
): number => {
  const { createdOn, status } = ticket;
  const [msLeft, setMsLeft] = useState(() =>
    ownTicketEditMsLeft({ createdOn, status }, Date.now())
  );
  useEffect(() => {
    const tick = () => {
      const left = ownTicketEditMsLeft({ createdOn, status }, Date.now());
      setMsLeft(left);
      return left;
    };
    if (tick() === 0) return;
    const timer = setInterval(() => {
      if (tick() === 0) clearInterval(timer);
    }, 5_000);
    return () => clearInterval(timer);
  }, [createdOn, status]);
  return msLeft;
};
